import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, ConfigProvider, Modal, theme as antdTheme } from 'antd';
import { formatBytes } from '../../services/SandboxService';
import { formatRelativeTime } from '../../utils/relativeTime';
import Disclosure from './Disclosure';
import useRememberedToggle from '../../hooks/useRememberedToggle';
import {
  backUpNow, cancelZipUpload, downloadMyBackup, getBackupProjects, getMyBackups, restoreMyBackup,
} from '../../services/MyBackupsService';
import {
  UploadActions, ZipUploadDialogBody, isUpload, listedBackups, useHiddenUploads, zipLayoutText, zipProjectNote,
} from './MyBackupsZip';
import './MyBackups.css';

/**
 * "Backups" in the person's Settings, Sandbox tab (pages/UserSettings.jsx): every backup of their own sandbox (the sandbox
 * admin's scheduled ones, an admin's, their own, the safety backups a restore takes) with its date, who made it, what
 * it holds and its size; "Back up now" (optional name), "Restore…" (everything or chosen projects, typed confirmation,
 * a safety backup first), "Download" (a zip of the projects' files) and "Restore from a zip…" (MyBackupsZip.jsx: a zip
 * uploaded becomes one more backup, then restored the same way; an uploaded zip is not a backup of the sandbox: it is
 * left out of the summary and the count, and has Discard, or "Remove from list" where the gateway cannot drop it). While a backup or restore runs, its progress,
 * refreshed every few seconds. Only ever the person's own sandbox (services/MyBackupsService.js, the gateway's
 * /api/v1/me/backups). Nothing shows where the gateway has no backups (older, shared mode, backups off).
 */

const POLL_MS = 5000;
const OPEN_STATUSES = ['queued', 'waiting', 'running'];
const SCAN_RETRY_MS = 3000;

/** "21 Oct 2026, 08:00". */
export const formatWhen = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—');

/** Who made a backup, in the person's words. */
export function madeByText(madeBy) {
  switch (madeBy?.kind) {
    case 'you': return 'You';
    case 'safety': return 'Safety backup';
    case 'schedule': return madeBy.plan ? `Scheduled · ${madeBy.plan}` : 'Scheduled';
    case 'migration': return 'Environment move';
    case 'imported': return 'Imported';
    case 'upload': return 'Uploaded zip';
    default: return 'Your sandbox admin';
  }
}

/** What a backup holds: "Projects and data", "Projects and data restore points", "Projects", "Data". */
export function holdsText(backup) {
  const { code, data } = backup?.holds || {};
  const projects = Array.isArray(backup?.projects) ? backup.projects.length : null;
  const what = projects !== null ? `${projects} project${projects === 1 ? '' : 's'}` : 'Projects';
  if (backup?.madeBy?.kind === 'upload' || backup?.zip) {
    const count = projects ?? backup?.zip?.projects ?? null;
    return count === null ? 'Projects (files only)' : `${count} project${count === 1 ? '' : 's'} (files only)`;
  }
  if (code && data === 'restore-points') { return `${what} and data restore points`; }
  if (code && data) { return `${what} and data`; }
  if (code) { return what; }
  if (data) { return data === 'restore-points' ? 'Data restore points' : 'Data'; }
  return '—';
}

const STATUS = {
  queued: { text: 'Queued', tone: 'building' },
  waiting: { text: 'Waiting', tone: 'warn' },
  running: { text: 'Running', tone: 'building' },
  uploading: { text: 'Uploading', tone: 'building' },
  succeeded: { text: 'Ready', tone: 'running' },
  failed: { text: 'Failed', tone: 'failed' },
  skipped: { text: 'Skipped', tone: 'warn' },
  cancelled: { text: 'Cancelled', tone: '' },
};

/** The collapsed line: the latest backup, when and how it went (an uploaded zip is not a backup of the sandbox). */
export function summaryOf(backups) {
  const latest = (backups || []).find((backup) => !isUpload(backup));
  if (!latest) { return 'No backups yet.'; }
  return `Latest ${formatRelativeTime(latest.createdAt)} · ${statusOf(latest).text}`;
}

export function statusOf(run) {
  return STATUS[run?.status] || { text: run?.status || '—', tone: '' };
}

// The restore's steps, by phase (the gateway's runner.js): safety backup, sleep, restore, wake up.
export const RESTORE_STEPS = ['Safety backup of what is replaced', 'Your sandbox goes to sleep', 'Restoring', 'It wakes up'];
const STEP_OF = { safety: 0, sleeping: 1, creating: 1, restoring: 2, waking: 3, done: 3 };

/** "123 MB of 500 MB (25%)", or the bytes alone when the total is not known; null without progress. */
export function bytesText(progress) {
  if (!progress || typeof progress.bytes !== 'number') { return null; }
  if (progress.total) {
    const percent = Math.min(100, Math.round((progress.bytes / progress.total) * 100));
    return `${formatBytes(progress.bytes)} of ${formatBytes(progress.total)} (${percent}%)`;
  }
  return formatBytes(progress.bytes);
}

/** One sentence for what runs now. */
export function progressText(active, { live = false } = {}) {
  if (!active) { return null; }
  if (active.kind === 'restore') {
    const step = STEP_OF[active.phase] ?? 1;
    return `Restoring the backup of ${formatWhen(active.backupCreatedAt)}: step ${step + 1} of ${RESTORE_STEPS.length}, ${RESTORE_STEPS[step].toLowerCase()}. Your sandbox wakes up by itself when it is done.`;
  }
  if (active.status === 'waiting') { return 'Backup waiting: it starts when your sandbox sleeps.'; }
  if (active.status === 'queued') { return 'Backup queued: it starts in a moment.'; }
  return active.live || live ? 'Backing up while your sandbox runs…' : 'Backing up…';
}

/** The person's backups, refreshed while something runs: `[data, setData, reload]`. */
export function useMyBackups({ pollMs = POLL_MS } = {}) {
  const [data, setData] = useState(undefined); // undefined: loading; null: no backups here
  const [error, setError] = useState(null);
  const alive = useRef(true);
  const reload = useCallback(async() => {
    try {
      const next = await getMyBackups();
      if (!alive.current) { return; }
      setData(next && next.enabled !== false ? next : null);
      setError(null);
    } catch (err) {
      if (alive.current) { setError(err); }
    }
  }, []);
  useEffect(() => {
    alive.current = true;
    reload();
    return () => { alive.current = false; };
  }, [reload]);
  const busy = Boolean(data && (data.active || (data.backups || []).some((b) => OPEN_STATUSES.includes(b.status))));
  useEffect(() => {
    if (!busy) { return undefined; }
    const timer = setInterval(() => {
      if (typeof document === 'undefined' || document.visibilityState !== 'hidden') { reload(); }
    }, pollMs);
    return () => clearInterval(timer);
  }, [busy, pollMs, reload]);
  return [data, setData, reload, error];
}

const isDark = () => typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark';

function Dialog({ open, title, onClose, busy, children }) {
  return (
    <ConfigProvider theme={{ algorithm: isDark() ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm }}>
      <Modal
        className="sbx-bk-dialog"
        destroyOnHidden
        footer={null}
        mask={{ closable: !busy }}
        onCancel={busy ? undefined : onClose}
        open={open}
        title={title}
        width={560}
      >
        {children}
      </Modal>
    </ConfigProvider>
  );
}

Dialog.propTypes = { open: PropTypes.bool, title: PropTypes.node.isRequired, onClose: PropTypes.func.isRequired, busy: PropTypes.bool, children: PropTypes.node };

/** "Back up now": an optional name, what happens while the sandbox runs. */
export function BackUpNowDialog({ open, data, onClose, onDone }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const nameId = useId();
  useEffect(() => { if (open) { setName(''); setError(null); } }, [open]);
  const submit = async(event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onDone(await backUpNow(name));
    } catch (err) {
      setError(err.message || 'The backup could not start.');
    } finally {
      setBusy(false);
    }
  };
  let when = 'Your sandbox sleeps: the backup copies it as it is, in a few minutes.';
  if (data?.state === 'running') {
    when = data.live
      ? 'Your sandbox keeps running: your projects are copied as they are now; of the data, your projects’ restore points (make one in a project’s Data tab first if you want today’s rows).'
      : 'Your sandbox’s storage cannot be copied while it runs: the backup starts when it goes to sleep.';
  }
  return (
    <Dialog busy={busy} onClose={onClose} open={open} title="Back up your sandbox now">
      <form noValidate onSubmit={submit}>
        <p className="sbx-bk-hint">{when}</p>
        <label className="sbx-bk-label" htmlFor={nameId}>Name (optional)</label>
        <input
          autoComplete="off"
          className="sbx-bk-input"
          disabled={busy}
          id={nameId}
          maxLength={80}
          onChange={(event) => setName(event.target.value)}
          placeholder="e.g. Before the demo"
          type="text"
          value={name}
        />
        {data?.limits && (
          <p className="sbx-bk-hint sbx-bk-small">
            {`Your plan: ${data.limits.manualPerDay} backup${data.limits.manualPerDay === 1 ? '' : 's'} of your own a day; the newest ${data.limits.keepLast} are kept for ${data.limits.keepDays} days.`}
          </p>
        )}
        {error && <p className="sbx-bk-error" role="alert">{error}</p>}
        <div className="sbx-bk-actions">
          <Button disabled={busy} onClick={onClose}>Cancel</Button>
          <Button htmlType="submit" loading={busy} type="primary">{busy ? 'Starting…' : 'Back up'}</Button>
        </div>
      </form>
    </Dialog>
  );
}

BackUpNowDialog.propTypes = { open: PropTypes.bool, data: PropTypes.object, onClose: PropTypes.func.isRequired, onDone: PropTypes.func.isRequired };

/** The name to type back: the project's (exactly one chosen), else the sandbox's. */
export function confirmWord({ mode, chosen, projects, sandboxName }) {
  if (mode === 'projects' && chosen.length === 1) {
    const one = (projects || []).find((p) => p.id === chosen[0]);
    return one ? (one.name || one.id) : sandboxName;
  }
  return sandboxName;
}

/** "Restore…": everything or chosen projects, what is replaced, the safety backup, the typed confirmation. */
export function RestoreDialog({ backup, data, onClose, onDone }) {
  const [mode, setMode] = useState('all');
  const [projects, setProjects] = useState(null); // null: not loaded
  const [projectsError, setProjectsError] = useState(null);
  const [chosen, setChosen] = useState([]);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const confirmId = useId();
  const descId = useId();
  const open = Boolean(backup);

  useEffect(() => {
    setMode('all'); setProjects(null); setProjectsError(null); setChosen([]); setTyped(''); setError(null);
  }, [backup?.id]);

  useEffect(() => {
    if (!backup || mode !== 'projects' || projects) { return undefined; }
    let cancelled = false;
    if (Array.isArray(backup.projects)) { setProjects(backup.projects); return undefined; }
    let timer = null;
    // A big backup's projects are read in the background (`scanning`): asked again every few seconds until listed.
    const ask = () => getBackupProjects(backup.id)
      .then((out) => {
        if (cancelled) { return; }
        if (out?.scanning && !Array.isArray(out.projects)) { timer = setTimeout(ask, SCAN_RETRY_MS); return; }
        setProjects(out?.projects || []);
      })
      .catch((err) => { if (!cancelled) { setProjects([]); setProjectsError(err.message); } });
    ask();
    return () => { cancelled = true; if (timer) { clearTimeout(timer); } };
  }, [backup, mode, projects]);

  if (!backup) { return <Dialog onClose={onClose} open={false} title="Restore" />; }
  const canChoose = Boolean(backup.holds?.code);
  const isZip = Boolean(backup.zip) || backup.madeBy?.kind === 'upload';
  const zipProjects = isZip && Array.isArray(backup.projects) ? backup.projects : [];
  const restoresPerDay = data?.limits?.restoresPerDay ?? null;
  const restoresLeft = restoresPerDay === null ? null : Math.max(0, restoresPerDay - (data?.used?.restores ?? 0));
  const word = confirmWord({ mode, chosen, projects, sandboxName: data?.sandboxName });
  const ready = (mode === 'all' || chosen.length > 0) && typed.trim() === word && restoresLeft !== 0;
  const toggle = (id) => setChosen((list) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]));

  const submit = async(event) => {
    event.preventDefault();
    if (!ready) { return; }
    setBusy(true);
    setError(null);
    try {
      onDone(await restoreMyBackup(backup.id, { confirm: typed.trim(), projects: mode === 'projects' ? chosen : null }));
    } catch (err) {
      setError(err.code === 'confirm_mismatch' ? 'That is not the name shown: type it exactly as it is.' : (err.message || 'The restore could not start.'));
    } finally {
      setBusy(false);
    }
  };
  const download = async(projectId) => {
    setDownloading(projectId);
    setError(null);
    try {
      await downloadMyBackup(backup.id, projectId);
    } catch (err) {
      setError(err.message);
    } finally {
      setDownloading(null);
    }
  };

  let replaced = mode === 'projects'
    ? 'The chosen projects’ files and history are replaced by this backup’s: what changed in them since then is lost. Every other project and all your data stay as they are (a project’s data comes back from its Data tab › Restore points).'
    : [
      `Everything in your sandbox is replaced by this backup: your projects’ files and history${backup.holds?.data ? ' and your data' : ''}. What changed since ${formatWhen(backup.createdAt)} is lost.`,
      backup.holds?.data === 'restore-points' ? ' This backup was taken while your sandbox ran: of the data, your projects’ restore points come back and the databases stay as they are.' : '',
    ].join('');
  if (isZip) {
    const count = mode === 'projects' ? chosen.length : zipProjects.length;
    replaced = [
      `${count} project${count === 1 ? '' : 's'} from the zip: those that replace a project in your sandbox get its files replaced by the zip’s (its history is kept, what changed since is lost from the current files); the others come in as new projects.`,
      ' Only files: your data, databases and app secrets are not touched, and every other project stays as it is.',
    ].join('');
  }

  return (
    <Dialog busy={busy} onClose={onClose} open={open} title={isZip ? (backup.zip?.filename ? `Restore the zip ${backup.zip.filename}?` : 'Restore the uploaded zip?') : `Restore the backup of ${formatWhen(backup.createdAt)}?`}>
      <form aria-describedby={descId} noValidate onSubmit={submit}>
        <div id={descId}>
          {canChoose && (
            <fieldset className="sbx-bk-modes">
              <legend className="sbx-bk-label">What to restore</legend>
              <label className="sbx-bk-choice">
                <input checked={mode === 'all'} disabled={busy} name="sbx-bk-mode" onChange={() => setMode('all')} type="radio" value="all" />
                {isZip ? 'Every project in the zip' : 'Everything'}
              </label>
              <label className="sbx-bk-choice">
                <input checked={mode === 'projects'} disabled={busy} name="sbx-bk-mode" onChange={() => setMode('projects')} type="radio" value="projects" />
                Only some projects
              </label>
            </fieldset>
          )}
          {isZip && zipLayoutText(backup.zip) && <p className="sbx-bk-hint">{zipLayoutText(backup.zip)}</p>}
          {isZip && mode === 'all' && zipProjects.length > 0 && (
            <ul aria-label="Projects in the zip" className="sbx-bk-projects">
              {zipProjects.map((project) => (
                <li key={project.id}>
                  <span>
                    <strong>{project.name || project.folder || project.id}</strong>
                    <span className="sbx-bk-meta">{` ${zipProjectNote(project)}`}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
          {mode === 'projects' && (
            projects === null
              ? <p className="sbx-bk-hint" role="status">Reading the backup’s projects…</p>
              : projects.length === 0
                ? <p className="sbx-bk-hint">{projectsError || 'This backup has no projects.'}</p>
                : (
                  <ul aria-label="Projects in this backup" className="sbx-bk-projects">
                    {projects.map((project) => (
                      <li key={project.id}>
                        <label className="sbx-bk-choice">
                          <input checked={chosen.includes(project.id)} disabled={busy} onChange={() => toggle(project.id)} type="checkbox" />
                          {project.name || project.id}
                          {isZip && <span className="sbx-bk-meta">{` ${zipProjectNote(project)}`}</span>}
                        </label>
                        {!isZip && (
                        <Button
                          aria-label={`Download ${project.name || project.id} as a zip`}
                          loading={downloading === project.id}
                          onClick={() => download(project.id)}
                          size="small"
                        >
                          Download
                        </Button>
                        )}
                      </li>
                    ))}
                  </ul>
                )
          )}
          <p className="sbx-bk-warning">{replaced}</p>
          <p className="sbx-bk-hint">
            First a safety backup of what is replaced is taken, so you can undo this: it shows in your list of backups.
            Your sandbox sleeps while the restore runs and wakes up by itself when it is done.
          </p>
          {restoresPerDay !== null && (
            <p className="sbx-bk-hint sbx-bk-small">
              {restoresLeft === 0
                ? 'You used today’s restores: try again tomorrow.'
                : `It counts toward your ${restoresPerDay} restore${restoresPerDay === 1 ? '' : 's'} a day (${restoresLeft} left today).`}
            </p>
          )}
        </div>
        <label className="sbx-bk-label" htmlFor={confirmId}>
          Type <strong className="sbx-bk-mono">{word}</strong> to confirm
        </label>
        <input
          autoComplete="off"
          className="sbx-bk-input"
          disabled={busy}
          id={confirmId}
          onChange={(event) => setTyped(event.target.value)}
          spellCheck={false}
          type="text"
          value={typed}
        />
        {error && <p className="sbx-bk-error" role="alert">{error}</p>}
        <div className="sbx-bk-actions">
          <Button disabled={busy} onClick={onClose}>Cancel</Button>
          <Button danger disabled={!ready} htmlType="submit" loading={busy} type="primary">
            {busy ? 'Starting…' : isZip ? 'Restore from the zip' : mode === 'projects' ? 'Replace these projects' : 'Replace and restore'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

RestoreDialog.propTypes = { backup: PropTypes.object, data: PropTypes.object, onClose: PropTypes.func.isRequired, onDone: PropTypes.func.isRequired };

function Progress({ active, live }) {
  if (!active) { return null; }
  const step = active.kind === 'restore' ? (STEP_OF[active.phase] ?? 1) : null;
  const bytes = bytesText(active.progress);
  return (
    <div className="sbx-bk-progress" role="status">
      <progress
        aria-label={active.kind === 'restore' ? 'Restore in progress' : 'Backup in progress'}
        {...(active.progress?.total ? { max: active.progress.total, value: Math.min(active.progress.bytes || 0, active.progress.total) } : {})}
      />
      <span>
        {progressText(active, { live })}
        {bytes && <span className="sbx-bk-bytes">{` ${bytes}`}</span>}
      </span>
      {step !== null && (
        <ol className="sbx-bk-steps">
          {RESTORE_STEPS.map((label, index) => (
            <li aria-current={index === step ? 'step' : undefined} className={index < step ? 'done' : index === step ? 'now' : undefined} key={label}>{label}</li>
          ))}
        </ol>
      )}
    </div>
  );
}

Progress.propTypes = { active: PropTypes.object, live: PropTypes.bool };

export default function MyBackups({ pollMs = POLL_MS, storageKey = 'flow.home.backupsOpen', initiallyOpen = false }) {
  const [data, setData, reload, loadError] = useMyBackups({ pollMs });
  const [backingUp, setBackingUp] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [zipping, setZipping] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);
  const [open, toggleOpen] = useRememberedToggle(storageKey, initiallyOpen);
  const [hiddenUploads, hideUpload] = useHiddenUploads();
  const [discarding, setDiscarding] = useState(null);

  if (data === undefined && !loadError) { return null; }
  if (!data && !loadError) { return null; }

  const heading = <h2 id="sbx-backups">Backups</h2>;
  if (!data) {
    return (
      <section aria-labelledby="sbx-backups" className="sbx-card sbx-home-section sbx-bk">
        <div className="sbx-home-section-head">{heading}</div>
        <p className="sbx-bk-error" role="alert">
          Your backups could not be loaded ({loadError.message}).
          {' '}
          <Button onClick={reload} size="small">Retry</Button>
        </p>
      </section>
    );
  }
  if (!data.configured) {
    return (
      <section aria-labelledby="sbx-backups" className="sbx-card sbx-home-section sbx-bk">
        <div className="sbx-home-section-head">{heading}</div>
        <p className="sbx-bk-hint">Backups are not set up on this Flow yet: ask your sandbox admin.</p>
      </section>
    );
  }

  const { limits = {}, used = {}, active = null } = data;
  const backups = listedBackups(data.backups || [], hiddenUploads);
  const backupCount = backups.filter((backup) => !isUpload(backup)).length;
  const ownOpen = backups.some((b) => b.madeBy?.kind === 'you' && OPEN_STATUSES.includes(b.status));
  const restoreRunning = active?.kind === 'restore';
  const backupsLeft = Math.max(0, (limits.manualPerDay ?? 0) - (used.backups ?? 0));
  const restoresLeft = Math.max(0, (limits.restoresPerDay ?? 0) - (used.restores ?? 0));
  let backUpBlocked = null;
  if (data.state === 'missing') { backUpBlocked = 'You have no sandbox to back up yet.'; }
  else if (restoreRunning) { backUpBlocked = 'A restore is running.'; }
  else if (ownOpen) { backUpBlocked = 'Your backup is on its way.'; }
  else if (limits.manualPerDay === 0) { backUpBlocked = 'Your plan does not include backups of your own.'; }
  else if (backupsLeft === 0) { backUpBlocked = 'You made today’s backups: try again tomorrow.'; }
  const restoreBlocked = Boolean(active) || restoresLeft === 0 || data.state === 'missing';
  const uploads = data.uploads || null;
  let zipBlocked = null;
  if (!uploads) { zipBlocked = 'Restoring from a zip is not available on this Flow.'; }
  else if (!uploads.allowed) { zipBlocked = uploads.reason || 'Restoring from a zip is not available on your plan.'; }
  else if (active) { zipBlocked = 'Wait for what runs now to finish.'; }
  else if (restoresLeft === 0) { zipBlocked = 'You used today’s restores: try again tomorrow.'; }
  else if (data.state === 'missing') { zipBlocked = 'You have no sandbox to restore into yet.'; }
  const afterZip = (out) => {
    setZipping(false);
    setError(null);
    if (!out) { return; }
    setData(out);
    const run = out.run || null;
    const listed = run ? (out.backups || []).find((b) => b.id === run.id) : null;
    setNotice('Zip checked: choose what to restore.');
    if (listed || run) { setRestoring(listed || run); }
  };

  const afterAction = (message) => (out) => {
    setBackingUp(false);
    setRestoring(null);
    setError(null);
    if (out) { setData(out); }
    setNotice(message);
  };
  const download = async(backup) => {
    setDownloading(backup.id);
    setError(null);
    try {
      await downloadMyBackup(backup.id);
    } catch (err) {
      setError(err.message);
    } finally {
      setDownloading(null);
    }
  };
  const discard = async(backup) => {
    setDiscarding(backup.id);
    setError(null);
    try {
      const out = await cancelZipUpload(backup.id);
      if (out) { setData(out); }
      setNotice('Uploaded zip discarded.');
    } catch (err) {
      setError(err.message);
    } finally {
      setDiscarding(null);
    }
  };
  const hide = (backup) => {
    hideUpload(backup.id);
    setNotice('Uploaded zip removed from the list.');
  };
  const recentRestores = (data.restores || []).filter((r) => r.status !== 'running').slice(0, 3);

  return (
    <section aria-labelledby="sbx-backups" className={`sbx-card sbx-home-section sbx-bk${open ? '' : ' sbx-bk-closed'}`}>
      {/* One line by default (backups matter less than what runs): the latest, Back up now, and the list behind a
          disclosure remembered for this viewer. What runs now and the outcome of an action show either way. */}
      <div className="sbx-bk-head">
        {heading}
        <span className="sbx-bk-summary">{summaryOf(backups)}</span>
        <div className="sbx-bk-head-actions">
          <Button disabled={Boolean(backUpBlocked)} onClick={() => { setNotice(null); setBackingUp(true); }} size="small" title={backUpBlocked || undefined}>
            Back up now
          </Button>
          <Button disabled={Boolean(zipBlocked)} onClick={() => { setNotice(null); setZipping(true); }} size="small" title={zipBlocked || undefined}>
            Restore from a zip…
          </Button>
          <Disclosure controls="sbx-bk-more" onToggle={toggleOpen} open={open}>
            {open ? 'Hide' : `All backups${backupCount ? ` (${backupCount})` : ''}`}
          </Disclosure>
        </div>
      </div>
      <Progress active={active} live={data.live} />
      <div aria-live="polite" className="sbx-bk-live">{notice}</div>
      {error && <p className="sbx-bk-error" role="alert">{error}</p>}
      <div className="sbx-bk-more" hidden={!open} id="sbx-bk-more">
      {open && (<>
      <p className="sbx-bk-hint sbx-bk-small">
        {limits.manualPerDay > 0
          ? `${used.backups ?? 0} of ${limits.manualPerDay} backups of your own today. Yours and safety backups: the newest ${limits.keepLast} are kept for ${limits.keepDays} days.`
          : 'Your sandbox admin’s backups are listed here; your plan does not include backups of your own.'}
        {backUpBlocked && limits.manualPerDay > 0 ? ` ${backUpBlocked}` : ''}
      </p>
      {backups.length === 0 ? (
        <p className="sbx-bk-hint">No backups of your sandbox yet.</p>
      ) : (
        <ul aria-label="Your backups" className="sbx-bk-list">
          {backups.map((backup) => {
            const status = statusOf(backup);
            const when = formatWhen(backup.createdAt);
            return (
              <li className="sbx-bk-item" key={backup.id}>
                <div className="sbx-bk-main">
                  <strong className="sbx-bk-when">
                    <time dateTime={backup.createdAt}>{when}</time>
                  </strong>
                  <span className="sbx-bk-meta">
                    {formatRelativeTime(backup.createdAt)}
                    {' · '}
                    {madeByText(backup.madeBy)}
                    {backup.name ? ` · “${backup.name}”` : ''}
                  </span>
                  <span className="sbx-bk-meta">
                    {holdsText(backup)}
                    {backup.sizeBytes ? ` · ${formatBytes(backup.sizeBytes)}` : ''}
                    {backup.live ? ' · taken while it ran' : ''}
                  </span>
                  {(backup.error || (backup.status !== 'succeeded' && backup.note)) && <span className="sbx-bk-meta sbx-bk-note">{backup.error || backup.note}</span>}
                </div>
                <span className={`sbx-pill${status.tone ? ` sbx-pill-${status.tone}` : ''}`}>{status.text}</span>
                <div className="sbx-bk-row-actions">
                  {backup.restorable && (
                    <Button
                      aria-label={`Restore the backup of ${when}`}
                      disabled={restoreBlocked}
                      onClick={() => { setNotice(null); setRestoring(backup); }}
                      size="small"
                      title={restoreBlocked ? (active ? 'Wait for what runs now to finish.' : 'No restores left today.') : undefined}
                    >
                      Restore…
                    </Button>
                  )}
                  {isUpload(backup) && (
                    <UploadActions backup={backup} discarding={discarding === backup.id} onDiscard={discard} onHide={hide} />
                  )}
                  {backup.downloadable && !isUpload(backup) && (
                    <Button
                      aria-label={`Download the backup of ${when} as a zip`}
                      loading={downloading === backup.id}
                      onClick={() => download(backup)}
                      size="small"
                    >
                      Download
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {recentRestores.length > 0 && (
        <div className="sbx-bk-restores">
          <h3>Recent restores</h3>
          <ul>
            {recentRestores.map((run) => (
              <li key={run.id}>
                <span className={`sbx-pill sbx-pill-${run.status === 'succeeded' ? 'running' : 'failed'}`}>{run.status === 'succeeded' ? 'Restored' : 'Failed'}</span>
                {' '}
                {run.projects?.length ? `${run.projects.length} project${run.projects.length === 1 ? '' : 's'}` : 'Everything'}
                {` from the backup of ${formatWhen(run.backupCreatedAt)}, ${formatRelativeTime(run.createdAt)}`}
                {run.error && <span className="sbx-bk-meta sbx-bk-note">{run.error}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
      </>)}
      </div>
      <BackUpNowDialog
        data={data}
        onClose={() => setBackingUp(false)}
        onDone={afterAction('Backup started: its progress shows here.')}
        open={backingUp}
      />
      <Dialog onClose={() => setZipping(false)} open={zipping} title="Restore from a zip">
        {zipping && <ZipUploadDialogBody onClose={() => setZipping(false)} onDone={afterZip} uploads={uploads} />}
      </Dialog>
      <RestoreDialog
        backup={restoring}
        data={data}
        onClose={() => setRestoring(null)}
        onDone={afterAction('Restore started: a safety backup is taken first, then your sandbox is restored.')}
      />
    </section>
  );
}

MyBackups.propTypes = { pollMs: PropTypes.number, storageKey: PropTypes.string, initiallyOpen: PropTypes.bool };
